import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { UtilityService } from 'src/app/service/utility.service';

interface DynamicComponent {
  cardDetails?: any;
  brandingValue?: boolean;
  headerBackground?: boolean;
  showHideMultiLanguage?: boolean;
}

@Component({
  selector: 'app-card-preview',
  templateUrl: './card-preview.component.html',
  styleUrls: ['./card-preview.component.scss'],
})
export class CardPreviewComponent implements OnChanges {
  @ViewChild('previewContainer', { read: ViewContainerRef, static: true })
  previewContainer!: ViewContainerRef;

  @ViewChild('scrollHost') scrollHost!: ElementRef<HTMLElement>;

  @Input() cardDetails: any = null;
  @Input() templateKey: string = ''; // e.g. 'contact-preview' or 'classic-pro'
  @Input() templateMode: number = 0; // 0 = light, 1 = dark (for card templates)
  @Input() brandingValue: boolean = true;
  @Input() headerBackground: boolean = false;

  private previewComponentRef: ComponentRef<DynamicComponent> | null = null;
  private lastTemplateKey = '';
  private loadRequestId = 0;

  private readonly componentMap: Record<string, () => Promise<any>> = {
    'classic-pro-0': () => import('src/app/templates/classic-pro/classic-pro.component').then(m => m.ClassicProComponent),
    'classic-pro-1': () => import('src/app/templates/classic-pro-dark/classic-pro-dark.component').then(m => m.ClassicProDarkComponent),
    'classic-elite-0': () => import('src/app/templates/classic-elite/classic-elite.component').then(m => m.ClassicEliteComponent),
    'classic-elite-1': () => import('src/app/templates/classic-elite-dark/classic-elite-dark.component').then(m => m.ClassicEliteDarkComponent),
    'cbd': () => import('src/app/templates/cbd/cbd.component').then(m => m.CbdComponent),
    'dp-world-0': () => import('src/app/templates/dp-world/dp-world.component').then(m => m.DpWorldComponent),
    'dp-world-1': () => import('src/app/templates/dp-world-dark/dp-world-dark.component').then(m => m.DpWorldDarkComponent),
    'signature-pro-0': () => import('src/app/templates/signature-pro/signature-pro.component').then(m => m.SignatureProComponent),
    'signature-pro-1': () => import('src/app/templates/signature-pro-dark/signature-pro-dark.component').then(m => m.SignatureProDarkComponent),
    'signature-elite-0': () => import('src/app/templates/signature-elite/signature-elite.component').then(m => m.SignatureEliteComponent),
    'signature-elite-1': () => import('src/app/templates/signature-elite-dark/signature-elite-dark.component').then(m => m.SignatureEliteDarkComponent),
      'norr': () => import('src/app/templates/norr/norr.component').then(m => m.NorrComponent),
      'executive-pro': () => import('src/app/templates/executive-pro/executive-pro.component').then(m => m.ExecutiveProComponent),
    };

  constructor(
    private resolver: ComponentFactoryResolver,
    private utilService: UtilityService,
  ) {}

  /** Phone frame + screen must use the card background so rounded corners match Appearance. */
  get previewFrameStyle(): Record<string, string> {
    const emptyDark =
      Number(this.cardDetails?.profile_status) === 0 && Number(this.cardDetails?.darkMode) === 1;
    if (this.templateMode === 1 || emptyDark) {
      return { 'background-color': '#000000', 'background-image': 'none' };
    }
    return this.utilService.getCardBackgroundStyle(this.cardDetails, '#FFFFFF');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.cardDetails || !this.templateKey) return;

    const effectiveKey = this.getEffectiveTemplateKey();
    const templateKeyChanged = effectiveKey !== this.lastTemplateKey;

    if (templateKeyChanged) {
      this.lastTemplateKey = effectiveKey;
      this.loadComponent();
      return;
    }

    if (this.previewComponentRef?.instance) {
      const instance = this.previewComponentRef.instance;
      instance.cardDetails = this.cardDetails;
      instance.brandingValue = this.brandingValue;
      instance.headerBackground = this.headerBackground;
      this.previewComponentRef.changeDetectorRef.detectChanges();
    }
  }

  onPreviewWheel(e: WheelEvent): void {
    const el = this.scrollHost?.nativeElement;
    if (!el) return;
    // Keep wheel scroll isolated to preview pane to avoid
    // simultaneous page + preview scrolling.
    e.preventDefault();
    e.stopPropagation();
    el.scrollTop += e.deltaY;
  }

  onPreviewKeydown(e: KeyboardEvent): void {
    const el = this.scrollHost?.nativeElement;
    if (!el) return;

    const step = 60;
    const page = Math.max(el.clientHeight - 40, step);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        el.scrollBy({ top: step, behavior: 'smooth' });
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        el.scrollBy({ top: -step, behavior: 'smooth' });
        break;
      case 'PageDown':
        e.preventDefault();
        e.stopPropagation();
        el.scrollBy({ top: page, behavior: 'smooth' });
        break;
      case 'PageUp':
        e.preventDefault();
        e.stopPropagation();
        el.scrollBy({ top: -page, behavior: 'smooth' });
        break;
      case 'Home':
        e.preventDefault();
        e.stopPropagation();
        el.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      case 'End':
        e.preventDefault();
        e.stopPropagation();
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        break;
    }
  }

  focusPreview(): void {
    this.scrollHost?.nativeElement?.focus();
  }

  scrollToTop(behavior: ScrollBehavior = 'auto'): void {
    const el = this.scrollHost?.nativeElement;
    if (!el) return;
    el.scrollTo({ top: 0, behavior });
  }

  /** Key used in componentMap: for contact/cbd/norr/executive-pro use key as-is; for card templates use key-mode. */
  private getEffectiveTemplateKey(): string {
    const noMode = ['contact-preview', 'cbd', 'norr', 'executive-pro'];
    if (noMode.includes(this.templateKey)) return this.templateKey;
    return `${this.templateKey}-${this.templateMode}`;
  }

  loadComponent(): void {
    if (!this.previewContainer || !this.templateKey) return;

    const currentRequestId = ++this.loadRequestId;
    this.previewComponentRef = null;
    this.previewContainer.clear();

    const effectiveKey = this.getEffectiveTemplateKey();
    const componentLoader = this.componentMap[effectiveKey];

    if (!componentLoader) {
      console.warn(`CardPreview: no template for key "${effectiveKey}"`);
      return;
    }

    componentLoader().then((Component) => {
      // Ignore stale async loads; only latest request may render.
      if (currentRequestId !== this.loadRequestId) {
        return;
      }
      this.previewContainer.clear();
      const factory = this.resolver.resolveComponentFactory(Component);
      const ref = this.previewContainer.createComponent(factory) as ComponentRef<DynamicComponent>;
      this.previewComponentRef = ref;
      const instance = ref.instance;
      instance.cardDetails = this.cardDetails;
      instance.brandingValue = this.brandingValue;
      instance.headerBackground = this.headerBackground;
      instance.showHideMultiLanguage = false;
      ref.changeDetectorRef.detectChanges();
    }).catch((err) => {
      console.error('CardPreview load error:', err);
    });
  }
}
